import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import PratusSelect from 'components/DesignSystems/New/PratusSelect';
import { USCG_SYMBOLOGY_OPTIONS, type UscgSymbol } from './utils/uscgSymbology';

type SymbologyOptionType = {
  value: string;
  label: string;
  iconSrc?: string;
};

function formatSymbologyOptionLabel(option: SymbologyOptionType) {
  return (
    <span className="d-flex align-items-center gap-2">
      {option.iconSrc && (
        <img src={option.iconSrc} alt="" className="symbology-icon" />
      )}
      <span>{option.label}</span>
    </span>
  );
}

type FormSymbologySelectProps = {
  name?: string;
  label?: string;
  excludeOptions?: UscgSymbol[];
  includeCustomPolygon?: boolean;
  onSymbologyChange?: (symbology: UscgSymbol | null) => void;
};

const FormSymbologySelect: React.FC<FormSymbologySelectProps> = ({
  name = 'symbology',
  label = 'Symbology',
  excludeOptions = [],
  includeCustomPolygon = false,
  onSymbologyChange,
}) => {
  const { control } = useFormContext();
  const options: SymbologyOptionType[] = useMemo(() => {
    const filtered = USCG_SYMBOLOGY_OPTIONS.filter(
      (opt) =>
        !excludeOptions.includes(opt.type) &&
        (includeCustomPolygon || opt.type !== 'customPolygon')
    ).map((opt) => ({
      value: opt.type,
      label: opt.label,
      iconSrc: opt.iconSrc,
    }));

    return [{ value: '', label: 'None' }, ...filtered];
  }, [excludeOptions, includeCustomPolygon]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        const option = options.find((o) => o.value === value) ?? options[0];

        return (
          <PratusSelect
            label={label}
            options={options}
            value={option}
            onChange={(opt) => {
              if (!opt || !('value' in opt) || typeof opt.value !== 'string') {
                return;
              }
              const newValue = opt.value as UscgSymbol;
              onChange(newValue);
              onSymbologyChange?.(newValue || null);
            }}
            formatOptionLabel={(data) =>
              formatSymbologyOptionLabel(data as SymbologyOptionType)
            }
          />
        );
      }}
    />
  );
};

export default FormSymbologySelect;
